import { type RangeAddr, type Span, type Token, tokenize } from "@package/scratchpad-utils";
import { scanCalls } from "./scan";

export const REF_COLORS = ["#4472C4", "#E4572E", "#70AD47", "#9E4FC4", "#2AA8A8", "#C4478F"] as const;

export type FormulaRef = { span: Span; addr: RangeAddr; color: string };

function refKey(addr: RangeAddr): string {
  const { start, end } = addr;
  const base = `${(start.sheet ?? "").toLowerCase()}!${start.col},${start.row}`;
  return end ? `${base}:${end.col},${end.row}` : base;
}

export function extractFormulaRefs(text: string): FormulaRef[] {
  if (!text.startsWith("=")) return [];
  let tokens: Token[];
  try {
    tokens = tokenize(text.slice(1));
  } catch {
    return [];
  }
  const colorByKey = new Map<string, string>();
  const refs: FormulaRef[] = [];
  for (const t of tokens) {
    if (t.kind !== "ref") continue;
    const key = refKey(t.addr);
    let color = colorByKey.get(key);
    if (!color) {
      color = REF_COLORS[colorByKey.size % REF_COLORS.length]!;
      colorByKey.set(key, color);
    }
    refs.push({ span: { start: t.span.start + 1, end: t.span.end + 1 }, addr: t.addr, color });
  }
  return refs;
}

export type PillKind = "product" | "field";

export type PillSpan = { span: Span; kind: PillKind };

export function extractCurvePillSpans(text: string): PillSpan[] {
  const out: PillSpan[] = [];
  for (const call of scanCalls(text)) {
    if (call.name?.toUpperCase() !== "CURVE") continue;
    const product = call.strings.find((s) => s.argIndex === 0);
    if (product) out.push({ span: { start: product.start, end: product.end }, kind: "product" });
    const field = call.strings.find((s) => s.argIndex === 1);
    if (field) out.push({ span: { start: field.start, end: field.end }, kind: "field" });
  }
  return out;
}

export type Segment = { start: number; text: string; color?: string; pill?: PillKind };

export function segmentFormula(text: string): Segment[] {
  const marks: Array<{ start: number; end: number; color?: string; pill?: PillKind }> = [
    ...extractFormulaRefs(text).map((r) => ({ start: r.span.start, end: r.span.end, color: r.color })),
    ...extractCurvePillSpans(text).map((p) => ({ start: p.span.start, end: p.span.end, pill: p.kind })),
  ].sort((a, b) => a.start - b.start);
  const segments: Segment[] = [];
  let pos = 0;
  for (const mark of marks) {
    if (mark.start > pos) segments.push({ start: pos, text: text.slice(pos, mark.start) });
    const segment: Segment = { start: mark.start, text: text.slice(mark.start, mark.end) };
    if (mark.color) segment.color = mark.color;
    if (mark.pill) segment.pill = mark.pill;
    segments.push(segment);
    pos = mark.end;
  }
  if (pos < text.length) segments.push({ start: pos, text: text.slice(pos) });
  return segments;
}
